'use client';

import { useState, useEffect } from 'react';
import FilterPanel, { FilterState } from './FilterPanel';
import ProductCard, { Product } from './ProductCard';
import SearchBar from './SearchBar';
import Icon from '@/components/ui/AppIcon';

const ProductListingInteractive = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [products, setProducts] = useState<Product[]>([]);
  const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [currentPage, setCurrentPage] = useState(1);
  const productsPerPage = 12;

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  useEffect(() => {
    if (isHydrated) {
      loadProducts();
    }
  }, [isHydrated]);

  const loadProducts = () => {
    setIsLoading(true);

    const mockProducts: Product[] = [
    {
      id: '1',
      name: 'Fresh Atlantic Salmon Fillet',
      category: 'Fresh Fish',
      price: 899.00,
      originalPrice: 1099.00,
      image: "https://images.unsplash.com/photo-1673767415033-b99c536cc370",
      alt: 'Fresh pink Atlantic salmon fillet on white surface with ice crystals',
      weight: '500g - 1kg',
      availability: 'in-stock',
      stockCount: 45,
      catchDate: '24/12/2025',
      rating: 4.8,
      reviewCount: 234,
      freshness: 98,
      badges: ['Premium', 'Wild Caught'],
      nutritionHighlights: ['High Omega-3', 'Protein Rich', 'Low Mercury']
    },
    {
      id: '2',
      name: 'Yellowfin Tuna Steaks',
      category: 'Fish Cuts',
      price: 1299.00,
      originalPrice: 1499.00,
      image: "https://images.unsplash.com/photo-1691120217303-dfc557ff9c4c",
      alt: 'Deep red yellowfin tuna steaks arranged on dark slate board',
      weight: '250g - 500g',
      availability: 'in-stock',
      stockCount: 32,
      catchDate: '25/12/2025',
      rating: 4.9,
      reviewCount: 189,
      freshness: 99,
      badges: ['Sushi Grade', 'Premium'],
      nutritionHighlights: ['High Protein', 'Vitamin B12', 'Selenium']
    },
    {
      id: '3',
      name: 'Tiger Prawns Large',
      category: 'Fresh Fish',
      price: 749.00,
      image: "https://images.unsplash.com/photo-1665262734761-065d7c2b1382",
      alt: 'Large tiger prawns with distinctive black stripes on ice bed',
      weight: '500g',
      availability: 'low-stock',
      stockCount: 8,
      catchDate: '24/12/2025',
      rating: 4.7,
      reviewCount: 156,
      freshness: 97,
      badges: ['Fresh Catch'],
      nutritionHighlights: ['Low Fat', 'High Protein', 'Vitamin D']
    },
    {
      id: '4',
      name: 'Mackerel Whole Fish',
      category: 'Fresh Fish',
      price: 349.00,
      originalPrice: 449.00,
      image: "https://images.unsplash.com/photo-1637088059585-2b76b56bdaac",
      alt: 'Whole mackerel fish with silver scales and blue-green stripes on market display',
      weight: '300g - 500g',
      availability: 'in-stock',
      stockCount: 67,
      catchDate: '25/12/2025',
      rating: 4.6,
      reviewCount: 203,
      freshness: 96,
      badges: ['Local Catch'],
      nutritionHighlights: ['Omega-3', 'Vitamin B12', 'Iron']
    },
    {
      id: '5',
      name: 'Cod Fish Fillets Boneless',
      category: 'Fish Cuts',
      price: 649.00,
      image: "https://images.unsplash.com/photo-1622925674383-92aa47e93452",
      alt: 'White boneless cod fish fillets on wooden cutting board',
      weight: '400g - 600g',
      availability: 'in-stock',
      stockCount: 28,
      catchDate: '24/12/2025',
      rating: 4.5,
      reviewCount: 142,
      freshness: 95,
      badges: ['Boneless'],
      nutritionHighlights: ['Low Calorie', 'High Protein', 'Phosphorus']
    },
    {
      id: '6',
      name: 'Sea Bass Whole',
      category: 'Fresh Fish',
      price: 1099.00,
      originalPrice: 1299.00,
      image: "https://images.unsplash.com/photo-1712064740743-f5d1cafbff97",
      alt: 'Whole sea bass fish with silver scales on ice display',
      weight: '600g - 800g',
      availability: 'in-stock',
      stockCount: 19,
      catchDate: '25/12/2025',
      rating: 4.8,
      reviewCount: 167,
      freshness: 98,
      badges: ['Premium', 'Wild Caught'],
      nutritionHighlights: ['Omega-3', 'Vitamin A', 'Selenium']
    },
    {
      id: '7',
      name: 'Sardines Fresh Pack',
      category: 'Fresh Fish',
      price: 249.00,
      image: "https://images.unsplash.com/photo-1684932581710-5e47833358d3",
      alt: 'Small silver sardines arranged in neat rows on market tray',
      weight: '500g',
      availability: 'in-stock',
      stockCount: 89,
      catchDate: '25/12/2025',
      rating: 4.4,
      reviewCount: 198,
      freshness: 97,
      badges: ['Budget Friendly'],
      nutritionHighlights: ['Calcium', 'Vitamin D', 'Omega-3']
    },
    {
      id: '8',
      name: 'Lobster Live',
      category: 'Fresh Fish',
      price: 2499.00,
      image: "https://images.unsplash.com/photo-1672529276568-b08e47b8784c",
      alt: 'Live lobster with red-brown shell and large claws on ice',
      weight: '800g - 1.2kg',
      availability: 'pre-order',
      stockCount: 5,
      catchDate: '26/12/2025',
      rating: 4.9,
      reviewCount: 87,
      freshness: 100,
      badges: ['Premium', 'Live'],
      nutritionHighlights: ['High Protein', 'Low Fat', 'Zinc']
    },
    {
      id: '9',
      name: 'Pomfret Silver',
      category: 'Fresh Fish',
      price: 799.00,
      originalPrice: 899.00,
      image: "https://images.unsplash.com/photo-1711649801345-59b4308d03b9",
      alt: 'Silver pomfret fish with flat round body on white background',
      weight: '400g - 600g',
      availability: 'in-stock',
      stockCount: 34,
      catchDate: '24/12/2025',
      rating: 4.7,
      reviewCount: 176,
      freshness: 96,
      badges: ['Local Favorite'],
      nutritionHighlights: ['Omega-3', 'Protein', 'Vitamin B12']
    },
    {
      id: '10',
      name: 'Crab Meat Fresh',
      category: 'Processed',
      price: 1599.00,
      image: "https://images.unsplash.com/photo-1693164319208-b962ea343597",
      alt: 'Fresh white crab meat pieces in clear container on ice',
      weight: '250g',
      availability: 'low-stock',
      stockCount: 6,
      catchDate: '25/12/2025',
      rating: 4.8,
      reviewCount: 134,
      freshness: 98,
      badges: ['Premium', 'Ready to Cook'],
      nutritionHighlights: ['High Protein', 'Low Calorie', 'Vitamin B12']
    },
    {
      id: '11',
      name: 'Squid Cleaned',
      category: 'Processed',
      price: 549.00,
      image: "https://images.unsplash.com/photo-1627703654841-bde876d64eee",
      alt: 'Cleaned white squid rings and tentacles on dark surface',
      weight: '500g',
      availability: 'in-stock',
      stockCount: 42,
      catchDate: '24/12/2025',
      rating: 4.5,
      reviewCount: 145,
      freshness: 95,
      badges: ['Cleaned', 'Ready to Cook'],
      nutritionHighlights: ['Low Fat', 'High Protein', 'Iron']
    },
    {
      id: '12',
      name: 'Anchovies Dried',
      category: 'Dried',
      price: 399.00,
      originalPrice: 499.00,
      image: "https://images.unsplash.com/photo-1649405087405-6ed3bfbaee3b",
      alt: 'Small dried anchovies in brown color arranged in pile',
      weight: '200g',
      availability: 'in-stock',
      stockCount: 78,
      catchDate: '20/12/2025',
      rating: 4.3,
      reviewCount: 112,
      freshness: 92,
      badges: ['Long Shelf Life'],
      nutritionHighlights: ['Calcium', 'Protein', 'Omega-3']
    }];


    setTimeout(() => {
      setProducts(mockProducts);
      setFilteredProducts(mockProducts);
      setIsLoading(false);
    }, 500);
  };

  const handleFilterChange = (filters: FilterState) => {
    let filtered = [...products];

    if (filters.categories.length > 0) {
      filtered = filtered.filter((product) =>
      filters.categories.some((cat) =>
      product.category.toLowerCase().includes(cat.replace('-', ' '))
      )
      );
    }

    if (filters.sizes.length > 0) {
      filtered = filtered.filter((product) => {
        const weight = product.weight.toLowerCase();
        return filters.sizes.some((size) => {
          if (size === 'small') return weight.includes('< 500g') || weight.includes('200g') || weight.includes('250g');
          if (size === 'medium') return weight.includes('500g') || weight.includes('400g') || weight.includes('600g');
          if (size === 'large') return weight.includes('> 1kg') || weight.includes('800g') || weight.includes('1kg') || weight.includes('1.2kg');
          return false;
        });
      });
    }

    if (filters.availability.length > 0) {
      filtered = filtered.filter((product) =>
      filters.availability.includes(product.availability)
      );
    }

    filtered = filtered.filter(
      (product) =>
      product.price >= filters.priceRange[0] &&
      product.price <= filters.priceRange[1]
    );

    switch (filters.sortBy) {
      case 'price-low':
        filtered.sort((a, b) => a.price - b.price);
        break;
      case 'price-high':
        filtered.sort((a, b) => b.price - a.price);
        break;
      case 'freshness':
        filtered.sort((a, b) => b.freshness - a.freshness);
        break;
      case 'popularity':
        filtered.sort((a, b) => b.reviewCount - a.reviewCount);
        break;
    }

    setFilteredProducts(filtered);
    setCurrentPage(1);
  };

  const handleSearch = (query: string) => {
    if (!query.trim()) {
      setFilteredProducts(products);
      return;
    }

    const searchResults = products.filter(
      (product) =>
      product.name.toLowerCase().includes(query.toLowerCase()) ||
      product.category.toLowerCase().includes(query.toLowerCase()) ||
      product.nutritionHighlights.some((highlight) =>
      highlight.toLowerCase().includes(query.toLowerCase())
      )
    );

    setFilteredProducts(searchResults);
    setCurrentPage(1);
  };

  const handleAddToCart = (productId: string, quantity: number) => {
    console.log(`Added product ${productId} with quantity ${quantity} to cart`);
  };

  if (!isHydrated) {
    return (
      <div className="min-h-screen bg-background pt-[120px] pb-16">
        <div className="container mx-auto px-6">
          <div className="animate-pulse space-y-6">
            <div className="h-12 bg-muted rounded-md w-full"></div>
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
              <div className="h-96 bg-muted rounded-md"></div>
              <div className="lg:col-span-3 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                {[...Array(6)].map((_, i) =>
                <div key={i} className="h-96 bg-muted rounded-md"></div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>);

  }

  const indexOfLastProduct = currentPage * productsPerPage;
  const indexOfFirstProduct = indexOfLastProduct - productsPerPage;
  const currentProducts = filteredProducts.slice(indexOfFirstProduct, indexOfLastProduct);
  const totalPages = Math.ceil(filteredProducts.length / productsPerPage);

  return (
    <div className="min-h-screen bg-background pt-[120px] pb-16">
      <div className="container mx-auto px-6">
        {/* Header Section */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="font-heading font-bold text-3xl text-foreground mb-2">
                Fresh Fish & Seafood
              </h1>
              <p className="text-muted-foreground">
                Discover our premium selection of fresh, hygienic fish products
              </p>
            </div>
            <div className="hidden md:flex items-center gap-2">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded-md transition-smooth ${
                viewMode === 'grid' ? 'bg-primary text-primary-foreground' : 'bg-card hover:bg-muted'}`
                }
                aria-label="Grid view">

                <Icon name="Squares2X2Icon" size={20} />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded-md transition-smooth ${
                viewMode === 'list' ? 'bg-primary text-primary-foreground' : 'bg-card hover:bg-muted'}`
                }
                aria-label="List view">

                <Icon name="ListBulletIcon" size={20} />
              </button>
            </div>
          </div>

          {/* Search Bar */}
          <SearchBar onSearch={handleSearch} />
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Filter Panel */}
          <div className="lg:col-span-1">
            <FilterPanel
              onFilterChange={handleFilterChange}
              resultCount={filteredProducts.length} />

          </div>

          {/* Products Grid */}
          <div className="lg:col-span-3">
            {isLoading ?
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                {[...Array(6)].map((_, i) =>
              <div key={i} className="animate-pulse">
                    <div className="h-48 bg-muted rounded-t-md"></div>
                    <div className="p-4 space-y-3 bg-card rounded-b-md">
                      <div className="h-4 bg-muted rounded w-3/4"></div>
                      <div className="h-4 bg-muted rounded w-1/2"></div>
                      <div className="h-8 bg-muted rounded"></div>
                    </div>
                  </div>
              )}
              </div> :
            filteredProducts.length === 0 ?
            <div className="flex flex-col items-center justify-center py-16 bg-card rounded-md border border-border">
                <Icon name="FaceFrownIcon" size={64} className="text-muted-foreground mb-4" />
                <h3 className="font-heading font-semibold text-xl text-foreground mb-2">
                  No Products Found
                </h3>
                <p className="text-muted-foreground text-center max-w-md">
                  We couldn't find any products matching your filters. Try adjusting your search criteria.
                </p>
              </div> :

            <>
                <div
                className={
                viewMode === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6' : 'space-y-4'
                }>

                  {currentProducts.map((product) =>
                <ProductCard
                  key={product.id}
                  product={product}
                  onAddToCart={handleAddToCart} />

                )}
                </div>

                {/* Pagination */}
                {totalPages > 1 &&
              <div className="flex items-center justify-center gap-2 mt-8">
                    <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="p-2 rounded-md border border-border hover:bg-muted transition-smooth disabled:opacity-50 disabled:cursor-not-allowed"
                  aria-label="Previous page">

                      <Icon name="ChevronLeftIcon" size={20} />
                    </button>
                    
                    {[...Array(totalPages)].map((_, index) => {
                  const pageNumber = index + 1;
                  if (
                  pageNumber === 1 ||
                  pageNumber === totalPages ||
                  pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)
                  {
                    return (
                      <button
                        key={pageNumber}
                        onClick={() => setCurrentPage(pageNumber)}
                        className={`px-4 py-2 rounded-md font-medium text-sm transition-smooth ${
                        currentPage === pageNumber ?
                        'bg-primary text-primary-foreground' :
                        'border border-border hover:bg-muted'}`
                        }>

                            {pageNumber}
                          </button>);

                  } else if (
                  pageNumber === currentPage - 2 ||
                  pageNumber === currentPage + 2)
                  {
                    return <span key={pageNumber} className="px-2">...</span>;
                  }
                  return null;
                })}

                    <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="p-2 rounded-md border border-border hover:bg-muted transition-smooth disabled:opacity-50 disabled:cursor-not-allowed"
                  aria-label="Next page">

                      <Icon name="ChevronRightIcon" size={20} />
                    </button>
                  </div>
              }
              </>
            }
          </div>
        </div>
      </div>
    </div>);

};

export default ProductListingInteractive;